import React from 'react'

import type { UserDocument } from '@hooks/use-ocr.hooks'
import Loader from '@ui/components/loader'
import { isDeviceMobile } from '@utils/mobile'
import { useUpload } from './pages/components/use-upload.hook'
import { UploadBox } from './pages/components/UploadBox'

type MultiUploadItem = {
	id: string
	file: File
}

type Props = {
	docId: UserDocument
	subStepId: number
	registerSave?: (fn: () => void) => void
	onDone: () => void
	setNextDisabled: (value: boolean) => void
	onStepLabelChange?: (label: string) => void
}

function getTexts(docId: UserDocument) {
	if (docId === 'CF') {
		return {
			title: 'Carica il documento fiscale',
			description:
				'Puoi allegare uno o più file. Dopo ogni caricamento vedrai l’anteprima del documento.',
			firstBoxLabel: 'Documento fiscale',
			nextBoxLabel: 'Aggiungi un altro file',
		}
	}

	if (docId === 'ALTRO') {
		return {
			title: 'Carica il documento di reddito',
			description:
				'Puoi allegare uno o più file. Dopo ogni caricamento vedrai l’anteprima del documento.',
			firstBoxLabel: 'Documento di reddito',
			nextBoxLabel: 'Aggiungi un altro file',
		}
	}

	return {
	  title: 'Allega o fotografa il documento',
    description:
    'Puoi allegare file in formato JPG, PNG, TIFF, HEIC/HEIF o PDF di dimensione non superiori a 5MB.I documenti devono essere chiaramente leggibili e non devono contenere riflessi.',
		firstBoxLabel: 'Documento',
		nextBoxLabel: 'Aggiungi un altro file',
	}
}

function FilePreview({ file }: { file: File }) {
	const url = React.useMemo(() => URL.createObjectURL(file), [file])

	React.useEffect(() => {
		return () => URL.revokeObjectURL(url)
	}, [url])

	return (
		<div className='w-full flex justify-center items-center border-2 border-dashed border-(--green-default) rounded-xl min-h-[215px]'>
			
			{file.type === 'application/pdf' ? (
									isDeviceMobile() ? (
										<div className='w-full flex flex-col items-center justify-center gap-4 p-4'>
											<div className='w-full text-center text-sm break-all'>
												{file.name}
											</div>
			
											<a
												href={url}
												target='_blank'
												rel='noopener noreferrer'
												className='
								w-full
								max-w-[320px]
								min-h-10
								px-8
								py-4
								flex
								items-center
								justify-center
								rounded-full
								bg-(--green-default)
								text-white
								text-[18px]
								font-semibold
								text-center
							'
											>
												Apri il documento
											</a>
										</div>
									) : (
										<iframe
											src={url}
											className='h-[215px] w-full max-w-[600px]'
											title='PDF preview'
										/>
									)
								) : (
									<img
										src={url}
										alt='Anteprima documento'
										className='h-[215px] max-w-full object-contain rounded-xl'
									/>
								)}
			{/* {file.type === 'application/pdf' ? (
				<iframe
					src={url}
					className='h-[215px] w-full rounded-xl'
					title='PDF preview'
				/>
			) : (
				<img
					src={url}
					alt='preview'
					className='h-[215px] rounded-xl object-contain'
				/>
			)} */}
		</div>
	)
}

export default function MobileMultiFileUploadPage({
	docId,
	subStepId,
	registerSave,
	onDone,
	setNextDisabled,
	onStepLabelChange
}: Props) {
	const {
		updateFiles,
		uploadFiles,
		uploadedFiles,
		removeFile,
		showLoader,
		hasError,
		canGoFurther,
	} = useUpload(docId, 20, 1)

	const texts = getTexts(docId)

	const [items, setItems] = React.useState<MultiUploadItem[]>([])
	const [previewFile, setPreviewFile] = React.useState<File | null>(null)
	const [previewSlotId, setPreviewSlotId] = React.useState<string | null>(null)

	const confirmPending = React.useRef(false)


	//STEP LABEL

	const stepLabel = React.useMemo(() => {
	if(!!previewFile){
		return isDeviceMobile()
		? 'Fotografa o allega il documento'
		: 'Allega documento'
	}

	if (items.length > 0) {
		return 'Le immagini sono pronte'
	}

	return 'Anteprima del documento'
}, [previewFile, items.length])


React.useEffect(() => {
	onStepLabelChange?.(stepLabel)
}, [stepLabel, onStepLabelChange])

console.log("PRE",!!previewFile,"item",items,stepLabel)

	// Footer disabled logic
	React.useEffect(() => {
		const hasConfirmedFiles = items.length > 0
		const isInPreview = !!previewFile

		// attivo:
		// - in preview (serve a confermare il file)
		// - ai box se ho almeno un file confermato
		const disabled = !isInPreview && !hasConfirmedFiles
		setNextDisabled(disabled)
	}, [items, previewFile, setNextDisabled])

	React.useEffect(() => {
		if (!confirmPending.current) return

		if (uploadedFiles && (uploadedFiles.DocumentsList?.length ?? 0) > 0) {
			confirmPending.current = false
			onDone()
		}
	}, [uploadedFiles, onDone])

	const handlePickFile = React.useCallback((file?: File, slotId?: string) => {
		if (!file || !slotId) return

		setPreviewFile(file)
		setPreviewSlotId(slotId)
	}, [])

	const handleRetryPreview = React.useCallback(() => {
		setPreviewFile(null)
		setPreviewSlotId(null)
	}, [])

	const handleConfirmPreview = React.useCallback(() => {
		if (!previewFile || !previewSlotId) return

		setItems((prev) => [
			...prev,
			{
				id: previewSlotId,
				file: previewFile,
			},
		])

		updateFiles(previewFile)

		setPreviewFile(null)
		setPreviewSlotId(null)
	}, [previewFile, previewSlotId, updateFiles])

	const handleRemoveConfirmed = React.useCallback(
		(itemId: string) => {
			const target = items.find((x) => x.id === itemId)
			if (target?.file) {
				removeFile(target.file.name)
			}

			setItems((prev) => prev.filter((item) => item.id !== itemId))
		},
		[items, removeFile]
	)

	const handleStartSaveFlow = React.useCallback(() => {
		if (showLoader) return
		if (!canGoFurther) return
		if (hasError.type !== 'NONE') return
		if (items.length === 0) return

		confirmPending.current = true
		uploadFiles()
	}, [showLoader, canGoFurther, hasError.type, items.length, uploadFiles])

	// Il parent chiama saveRef.current() quando premi Avanti
	React.useEffect(() => {
		if (!registerSave) return

		registerSave(() => {
			if (previewFile) {
				handleConfirmPreview()
				return
			}

			handleStartSaveFlow()
		})
	}, [registerSave, previewFile, handleConfirmPreview, handleStartSaveFlow])

	if (previewFile) {
		return (
			<div className='w-full max-w-[1296px] mx-auto pt-5 md:pt-14 px-3 md:px-6 flex flex-col gap-6'>
				<Loader isOpen={showLoader} />

				<div>
					<div className='mb-4 md:mb-6 text-[24px] md:text-[28px] font-semibold text-(--primary-blue)'>
						Anteprima del documento
					</div>

					<p className='font-medium text-[13px] md:text-[16px]'>
						Se la foto ti soddisfa prosegui, altrimenti riprova
					</p>
				</div>

				<FilePreview file={previewFile} />
				<div className='w-full flex justify-center'>
				<button
					type='button'
					onClick={handleRetryPreview}
					className='underline text-(--green-default) font-bold cursor-pointer w-fit'
				>
					Riprova
				</button>
				</div>
			</div>
		)
	}

	return (
		<div className='w-full max-w-[1296px] mx-auto pt-5 md:pt-14 px-3 md:px-6 flex flex-col gap-6'>
			<Loader isOpen={showLoader} />

			<div>
				<div className='mb-4 md:mb-6 text-[24px] md:text-[28px] font-semibold text-(--primary-blue)'>
					{texts.title}
				</div>

				<p className='font-medium text-[13px] md:text-[16px]'>
					{texts.description}
				</p>

				<p className='font-medium text-[13px] md:text-[16px] mt-2'>
					Puoi allegare file in formato JPG, PNG, TIFF, HEIC/HEIF o PDF di
					dimensione non superiori a 5MB. I documenti devono essere
					chiaramente leggibili e non devono contenere riflessi.
				</p>
			</div>

			{hasError?.type !== 'NONE' && (
				<div className='mt-3 p-3 rounded-md border border-(--text-red-alert)'>
					<div className='font-semibold'>{hasError.title}</div>
					<div>{hasError.description}</div>
				</div>
			)}

			<div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
				{items.map((item, index) => (
					<UploadBox
						key={item.id}
					    title={texts.firstBoxLabel}
						file={item.file}
						onFile={() => {}}
						onRemove={() => handleRemoveConfirmed(item.id)}
						mode={isDeviceMobile() ? 'camera' : 'upload'}
					/>
				))}

				<UploadBox
					key='empty-slot'
					title={
						items.length === 0 ? texts.firstBoxLabel : texts.nextBoxLabel
					}
					file={null}
					onFile={(file) => handlePickFile(file, crypto.randomUUID())}
					onRemove={() => {}}
					mode={isDeviceMobile() ? 'camera' : 'upload'}
				/>
			</div>
		</div>
	)
}